import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Pencil } from "lucide-react";

const ROLE_LABEL = {
  super_admin: "SUPER ADMIN",
  direktur: "DIREKTUR",
  manager: "MANAGER",
  admin: "ADMIN",
};

const ROLE_COLOR = {
  super_admin: "status-hot",
  direktur: "status-followup",
  manager: "status-warm",
  admin: "status-cold",
};

export default function UsersPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "admin", password: "", active: true });

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name, role: u.role, password: "", active: u.active });
  };
  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name, role: editForm.role, active: editForm.active };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "admin" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Team</div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">
            User management
          </h1>
        </div>
        {isSuper && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-button" className="bg-primary hover:bg-[#0056B3]">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                New user
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-border">
              <DialogHeader>
                <DialogTitle className="font-heading">Create user</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Name</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-border" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Email</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-border" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Password</label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0A] border-border" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Role</label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="direktur">Direktur</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-border bg-transparent">Cancel</Button>
                <Button data-testid="save-user-button" onClick={create} disabled={saving} className="bg-primary hover:bg-[#0056B3]">
                  {saving ? "Saving..." : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="bg-[#121212] border-border rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0A0A0A] border-b border-border">
            <tr>
              <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-left px-4 py-3">Name</th>
              <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-left px-4 py-3">Email</th>
              <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-left px-4 py-3">Role</th>
              <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-left px-4 py-3">Status</th>
              {isSuper && <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border hover:bg-[#1C1C1E] transition-colors">
                <td className="px-4 py-3 text-sm text-white font-medium">{u.name}</td>
                <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-mono ${ROLE_COLOR[u.role]}`}>
                    {ROLE_LABEL[u.role]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    disabled={!isSuper}
                    onClick={() => toggleActive(u)}
                    className={`text-xs font-mono px-2 py-0.5 rounded-sm border ${u.active ? "status-followup" : "border-border text-muted-foreground"}`}
                  >
                    {u.active ? "ACTIVE" : "INACTIVE"}
                  </button>
                </td>
                {isSuper && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 transition-all">
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      {u.id !== user.id && (
                        <button onClick={() => remove(u.id)} className="p-1.5 rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20 transition-all">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#121212] border-border">
          <DialogHeader><DialogTitle className="font-heading">Edit user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Name</label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-[#0A0A0A] border-border" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">New password (optional)</label>
              <Input type="password" placeholder="Leave blank to keep current" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="bg-[#0A0A0A] border-border" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Role</label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="direktur">Direktur</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-border bg-transparent">Cancel</Button>
            <Button data-testid="save-edit-user-button" onClick={saveEdit} className="bg-primary hover:bg-[#0056B3]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
